// trustAudit.ts — per-response groundedness verdict for Pro Mode.
//
// Pro Mode swaps the browser ReAct loop for Groq Llama 3.3 70B with
// native function calling. The grounding gap that fix #3 closes: the
// model can stream a confident answer that draws on tool-call outputs
// our corpus doesn't have, and the existing post-response groundedness
// check (browser path) doesn't run. This module restores that check on
// the browser side after the stream completes — same checker as the
// browser path, so the verdict is coherent across modes.
//
// No LLM judge in v1 — strict embedding cosine + entity grounding only,
// per the verifier's note. The numbers are mechanical and reproducible.

import type { Chunk } from "@/data/corpus";
import {
  semanticGrounding,
  entityGrounding,
  splitSentences,
  THRESHOLDS,
} from "@/lib/groundedness";

export type TrustVerdict = {
  // Headline: was every sentence supported by at least one corpus chunk
  // OR a tool-call output the model received during this turn?
  overall: "grounded" | "partial" | "unverified";
  groundedSentences: number;
  partialSentences: number; // below threshold but plausible (0.3-threshold)
  totalSentences: number;
  // Number of tool calls the agent made during this turn (from the trace).
  // Surfaced separately because tool output can ground sentences the
  // corpus alone can't (e.g. "MCP-JEST has 17 stars" is grounded by the
  // tool result even though the corpus doesn't know today's star count).
  toolCalls: number;
  // Entity-grounding unsupported tokens — proper-noun shapes the corpus
  // doesn't contain. Often empty for honest answers; non-empty for
  // hallucinations.
  unsupportedEntities: string[];
  perSentence: Array<{
    sentence: string;
    maxCosine: number;
    supportedBy: string | null; // chunk id
    status: "grounded" | "partial" | "unverified";
  }>;
};

export async function computeTrustVerdict(opts: {
  response: string;
  chunks: Chunk[];
  chunkEmbeddings: Float32Array[];
  toolCalls: number;
  // Raw tool outputs the agent received this turn. Folded into the
  // entity-grounding context so values returned by tools (star counts,
  // commit SHAs, version strings) don't count as "unsupported".
  toolOutputs?: unknown[];
}): Promise<TrustVerdict> {
  const { response, chunks, chunkEmbeddings, toolCalls, toolOutputs = [] } = opts;

  // No usable signal — return a neutral "unverified" instead of lying.
  if (chunkEmbeddings.length === 0) {
    const sentences = splitSentences(response);
    return {
      overall: "unverified",
      groundedSentences: 0,
      partialSentences: 0,
      totalSentences: sentences.length,
      toolCalls,
      unsupportedEntities: [],
      perSentence: sentences.map((s) => ({
        sentence: s,
        maxCosine: 0,
        supportedBy: null,
        status: "unverified",
      })),
    };
  }

  const sem = await semanticGrounding(response, chunks, chunkEmbeddings);

  // Build an augmented chunks list for entity grounding by appending a
  // pseudo-chunk whose text is the concatenated tool outputs. The proper
  // nouns / numbers tools returned this turn are then "in context" too.
  const toolText = toolOutputs
    .map((o) => safeStringify(o))
    .filter((s) => s.length > 0)
    .join(" ");
  const augmented: Chunk[] =
    toolText.length > 0
      ? [
          ...chunks,
          {
            id: "tool:output",
            text: toolText,
            category: "meta",
            tags: ["tool-output"],
          },
        ]
      : chunks;
  const ent = entityGrounding(response, augmented);

  // Three buckets per sentence:
  //   grounded  — maxCosine >= threshold
  //   partial   — within 0.10 below threshold (close enough to maybe match)
  //   unverified— well below threshold (suspicious)
  const partialFloor = Math.max(0, THRESHOLDS.minSentenceMaxCosine - 0.1);
  const perSentence = sem.perSentence.map((s) => {
    let status: "grounded" | "partial" | "unverified";
    if (s.maxCosine >= THRESHOLDS.minSentenceMaxCosine) status = "grounded";
    else if (s.maxCosine >= partialFloor) status = "partial";
    else status = "unverified";
    return { ...s, status };
  });

  const groundedSentences = perSentence.filter((s) => s.status === "grounded").length;
  const partialSentences = perSentence.filter((s) => s.status === "partial").length;
  const total = perSentence.length || 1;

  // Combined verdict — sentence cosine alone misses "right topic, wrong
  // fact" hallucinations (a sentence ABOUT Plum scored against Plum
  // chunks looks fine even if it invents a date). The entity check
  // catches those. So the headline must reflect both:
  //
  //   any unsupported entity that looks factual → unverified (red)
  //   any unsupported entity at all              → at most partial (amber)
  //   any sentence below partial floor           → unverified
  //   else                                       → grounded
  let overall: "grounded" | "partial" | "unverified";
  const anyUnverifiedSentence = perSentence.some((s) => s.status === "unverified");
  const factualUnsupported = ent.unsupported.some((tok) => looksFactual(tok));
  if (anyUnverifiedSentence || factualUnsupported) {
    overall = "unverified";
  } else if (ent.unsupported.length > 0) {
    overall = "partial";
  } else if (groundedSentences === total) {
    overall = "grounded";
  } else {
    overall = "partial";
  }

  return {
    overall,
    groundedSentences,
    partialSentences,
    totalSentences: perSentence.length,
    toolCalls,
    unsupportedEntities: ent.unsupported,
    perSentence,
  };
}

// ─────────────────────────── helpers ───────────────────────────

function safeStringify(v: unknown): string {
  try {
    return typeof v === "string" ? v : JSON.stringify(v);
  } catch {
    return "";
  }
}

// "Looks factual" = the token is the kind of value a hallucination is
// dangerous about. Dates, ISO timestamps, numbers, money, percentages,
// commit SHAs. Plain proper-noun-shaped tokens (e.g. "Coupang") are less
// dangerous when unsupported because they're often topic anchors, not
// claims the user will act on.
function looksFactual(tok: string): boolean {
  if (/^\d{4}-\d{2}-\d{2}/.test(tok)) return true; // ISO date
  if (/^\d{4}$/.test(tok)) return true; // year alone
  if (/^[A-Fa-f0-9]{7,40}$/.test(tok)) return true; // commit SHA
  if (/^\$?\d[\d,]*(?:\.\d+)?[KMB%+]?$/.test(tok)) return true; // amounts/percentages
  if (/^\d+(?:\.\d+)+$/.test(tok)) return true; // version numbers like 1.2.3
  return false;
}
